/**
 * Cell Types for Cell Swap Game
 * 
 * Defines different cell types for various game modes:
 * - Puyo colors (Red, Blue, Green, Yellow, Purple, Platelets) for Endless, Time Attack, and Classic modes
 * - Medical cell types (RBC, WBC, Stem, Donor, Cancer) for Puzzle mode (Dr. Mario style)
 */

export enum CellType {
  // Puyo Puyo colors for standard modes
  RED = 'red',
  BLUE = 'blue',
  GREEN = 'green',
  YELLOW = 'yellow',
  PURPLE = 'purple',
  PLATELETS = 'platelets',

  // Medical cell types for Puzzle mode
  RBC = 'rbc',           // Red Blood Cell
  WBC = 'wbc',           // White Blood Cell  
  STEM = 'stem',         // Stem Cell (wildcard)
  DONOR = 'donor',       // Donor Cell
  CANCER = 'cancer'      // Cancer Cell (virus)
}

export interface CellProperties {
  color: string
  displayName: string
  isWildcard: boolean
  canBeMatched: boolean
  canClearCancer: boolean // Legacy property, not used in Puyo mode
  scoreValue: number
  description: string
}

export const CELL_PROPERTIES: Record<CellType, CellProperties> = {
  // Puyo colors for standard modes
  [CellType.RED]: {
    color: '#FF4444', // Bright Red
    displayName: 'Red Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Red colored Puyo blob'
  },
  [CellType.BLUE]: {
    color: '#4444FF', // Bright Blue
    displayName: 'Blue Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Blue colored Puyo blob'
  },
  [CellType.GREEN]: {
    color: '#44FF44', // Bright Green
    displayName: 'Green Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Green colored Puyo blob'
  },
  [CellType.YELLOW]: {
    color: '#FFFF44', // Bright Yellow
    displayName: 'Yellow Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Yellow colored Puyo blob'
  },
  [CellType.PURPLE]: {
    color: '#FF44FF', // Bright Purple
    displayName: 'Purple Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Purple colored Puyo blob'
  },
  [CellType.PLATELETS]: {
    color: '#FFA500', // Orange
    displayName: 'Platelet Puyo',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Orange colored Puyo blob'
  },

  // Medical cell types for Puzzle mode
  [CellType.RBC]: {
    color: '#FF6B6B', // Red Blood Cell color
    displayName: 'Red Blood Cell',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 10,
    description: 'Healthy red blood cell that carries oxygen'
  },
  [CellType.WBC]: {
    color: '#4ECDC4', // White Blood Cell color
    displayName: 'White Blood Cell',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: true,
    scoreValue: 15,
    description: 'Immune cell that fights infections and cancer'
  },
  [CellType.STEM]: {
    color: '#45B7D1', // Stem Cell color
    displayName: 'Stem Cell',
    isWildcard: true,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 20,
    description: 'Versatile stem cell that can match with any type'
  },
  [CellType.DONOR]: {
    color: '#F7DC6F', // Donor Cell color
    displayName: 'Donor Cell',
    isWildcard: false,
    canBeMatched: true,
    canClearCancer: false,
    scoreValue: 12,
    description: 'Healthy donor cell from transplant tissue'
  },
  [CellType.CANCER]: {
    color: '#8B0000', // Dark Red
    displayName: 'Cancer Cell',
    isWildcard: false,
    canBeMatched: false, // Cancer cells cannot be matched directly
    canClearCancer: false,
    scoreValue: 0, // No points for cancer cells themselves
    description: 'Malignant cell that must be eliminated by clearing adjacent cells'
  }
}

export enum CellState {
  IDLE = 'idle',
  BLINK = 'blink',
  CLEAR = 'clear',
  FALLING = 'falling',
  MATCHED = 'matched'
}

export interface Cell {
  type: CellType
  state: CellState
  x: number
  y: number
  id: string
  animationFrame: number
  markedForDeletion: boolean
  capsulePairId?: string // Unique ID shared by both halves of a Dr. Mario capsule
  capsulePartner?: string // Cell ID of the other half of the capsule
}

/**
 * Helper functions for cell operations
 */
export class CellUtils {
  static createCell(type: CellType, x: number, y: number): Cell {
    return {
      type,
      state: CellState.IDLE,
      x,
      y,
      id: `${type}_${x}_${y}_${Date.now()}`,
      animationFrame: 0,
      markedForDeletion: false
    }
  }

  static getProperties(type: CellType): CellProperties {
    return CELL_PROPERTIES[type]
  }

  static canMatch(type1: CellType, type2: CellType): boolean {
    // Stem cells are wildcards that can match with any type
    if (type1 === CellType.STEM || type2 === CellType.STEM) {
      return true
    }

    // Otherwise, cells must be exactly the same type to match
    return type1 === type2
  }

  static canClearCancer(type: CellType): boolean {
    // Legacy function - no cancer clearing in Puyo mode
    return false
  }

  static getBonusMultiplier(type1: CellType, type2: CellType): number {
    // All Puyo colors have equal value, no special bonuses
    return 1.0
  }

  static getRandomMatchableType(): CellType {
    const puyoTypes = [CellType.RED, CellType.BLUE, CellType.GREEN, CellType.YELLOW, CellType.PURPLE, CellType.PLATELETS]
    return puyoTypes[Math.floor(Math.random() * puyoTypes.length)]
  }

  static getScoreValue(type: CellType): number {
    return CELL_PROPERTIES[type].scoreValue
  }
}